import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { useCtxDrivers } from '../../formulas/queries/useCtxDrivers'

/**
 * Hook to navigate to driver data page with formula IDs extracted from budgeting method source
 * Extracts variable() calls from source string and finds matching formula IDs
 */
export const useNavigateToDriverData = () => {
  const navigate = useNavigate()
  const { data: formulas, refetch } = useCtxDrivers()

  const navigateToDriverData = useCallback(
    async (row?: EnhancedStatementRow) => {
      if (!row?.budgetingMethod?.source) {
        console.warn('No budgeting method source found')
        return
      }

      // Show loading message
      const hideLoading = message.loading('Ladataan ajureita...', 0)

      try {
        // If formulas not loaded, fetch them
        let currentFormulas = formulas
        if (!currentFormulas || currentFormulas.length === 0) {
          const result = await refetch()
          currentFormulas = result.data
        }

        if (!currentFormulas || currentFormulas.length === 0) {
          hideLoading()
          message.warning('Ajureita ei löytynyt')
          return
        }

        const { source } = row.budgetingMethod
        const regex = /variable\('([^']+)'/g

        const formulaIds: number[] = []

        // matchAll returns an iterator; Array.from converts it to an array
        const matches = Array.from(source.matchAll(regex))

        for (const m of matches) {
          const code = m[1]
          const found = currentFormulas.find(f => f.code === code)
          if (found?.id != null) {
            formulaIds.push(found.id)
          }
        }

        // Include dimensionId in state if present on the row
        const dimensionIds = row.dimensionId ? [row.dimensionId] : undefined

        // Hide loading and navigate
        hideLoading()
        navigate('/budgeting/drivers', {
          state: {
            formulaIds,
            dimensionIds
          }
        })
      } catch (error) {
        console.error('Error loading formulas:', error)
        hideLoading()
        message.error('Virhe ladattaessa ajureita')
      }
    },
    [navigate, formulas, refetch]
  )

  return navigateToDriverData
}
